import type { Lang } from "@/i18n/dict";

/**
 * Digits stay LATIN in both languages.
 *
 * The marketing site sets Arabic-Indic numerals, and it is right to: it is
 * prose. This is not prose. An operator reads a figure here and copies it onto
 * a delivery note, into a spreadsheet, or into a WhatsApp message to a supplier
 * — all of which are Latin-digit. Two numbering systems in one workflow is a
 * transcription error waiting to happen, and this product exists to remove
 * exactly that class of error. IBM Plex Mono also has no Arabic-Indic digits,
 * so tabular alignment would break with them.
 */
const NUM_LOCALE: Record<Lang, string> = {
  ar: "ar-IQ-u-nu-latn",
  en: "en-GB",
};

const DATE_LOCALE: Record<Lang, string> = {
  ar: "ar-IQ-u-nu-latn-ca-gregory",
  en: "en-GB",
};

export function formatNumber(lang: Lang, value: number, maxFractionDigits = 0): string {
  return new Intl.NumberFormat(NUM_LOCALE[lang], {
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

/** Liters, the unit the whole product is denominated in. */
export function formatLiters(lang: Lang, value: number): string {
  return formatNumber(lang, value, value % 1 === 0 ? 0 : 1);
}

export function formatPercent(lang: Lang, value: number): string {
  return `${formatNumber(lang, value, value % 1 === 0 ? 0 : 1)}%`;
}

export function formatHours(lang: Lang, value: number): string {
  return formatNumber(lang, value, 1);
}

export function formatDate(lang: Lang, iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(DATE_LOCALE[lang], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

export function formatDateTime(lang: Lang, iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(DATE_LOCALE[lang], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** "in 3 days" / "4 days ago" — the unit a fuel runway is actually read in. */
export function formatRelativeDays(lang: Lang, days: number): string {
  const rtf = new Intl.RelativeTimeFormat(NUM_LOCALE[lang], { numeric: "auto" });
  return rtf.format(Math.round(days), "day");
}

/**
 * "2 minutes ago" / "just now" — how the age of a live reading is actually read.
 * `minutesAgo` is always the age (non-negative): a tile never has a reading from the
 * future, so unlike `formatRelativeDays` there is no signed direction to get wrong here.
 */
export function formatRelativeMinutes(lang: Lang, minutesAgo: number): string {
  const rtf = new Intl.RelativeTimeFormat(NUM_LOCALE[lang], { numeric: "auto" });
  const clamped = Math.max(0, Math.round(minutesAgo));
  if (clamped < 60) return rtf.format(-clamped, "minute");
  const hours = clamped / 60;
  // A device that has never reported, or reported before this fixture's own narrative
  // window, would otherwise print "14,208 hours ago" — technically true, and useless.
  // Beyond two days this is a date, not a countdown, so it drops to whole days.
  if (hours < 48) return rtf.format(-Math.round(hours), "hour");
  return rtf.format(-Math.round(hours / 24), "day");
}

/**
 * "8 seconds ago" — for a field board, whose `online` window is 30 seconds, minutes are too
 * coarse: "this minute" cannot tell a board that just synced from one that is about to drop.
 * Past a minute it reads exactly like every other age in the product.
 */
export function formatRelativeSeconds(lang: Lang, secondsAgo: number): string {
  const clamped = Math.max(0, Math.round(secondsAgo));
  if (clamped >= 60) return formatRelativeMinutes(lang, clamped / 60);
  const rtf = new Intl.RelativeTimeFormat(NUM_LOCALE[lang], { numeric: "auto" });
  return rtf.format(-clamped, "second");
}

/** An uptime: the two largest units that matter — "3 d 4 h", "17 min 12 s". */
export function formatDuration(lang: Lang, totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const parts: [number, "day" | "hour" | "minute" | "second"][] = [
    [Math.floor(seconds / 86_400), "day"],
    [Math.floor((seconds % 86_400) / 3_600), "hour"],
    [Math.floor((seconds % 3_600) / 60), "minute"],
    [seconds % 60, "second"],
  ];
  const first = parts.findIndex(([value]) => value > 0);
  const shown = first < 0 ? [parts[3]!] : parts.slice(first, first + 2);
  return shown
    .map(([value, unit]) =>
      new Intl.NumberFormat(NUM_LOCALE[lang], { style: "unit", unit, unitDisplay: "narrow" }).format(
        value,
      ),
    )
    .join(" ");
}

/** A file size, in the largest unit that keeps the figure readable. */
export function formatBytes(lang: Lang, bytes: number): string {
  const units = ["byte", "kilobyte", "megabyte"] as const;
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return new Intl.NumberFormat(NUM_LOCALE[lang], {
    style: "unit",
    unit: units[index],
    unitDisplay: "short",
    maximumFractionDigits: index === 0 ? 0 : 1,
  }).format(value);
}

/** `<input type="datetime-local">` wants a naive local string, not an ISO instant. */
export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function fromDateTimeLocalValue(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

export function toDateValue(date: Date): string {
  return toDateTimeLocalValue(date).slice(0, 10);
}
